<script setup>
import { FilterPanel } from '@dashhudson/dashing-ui/filter-panel';
import { SearchSelectList } from '@dashhudson/dashing-ui/search-select-list';
import { computed, onMounted, ref, watch } from 'vue';

import { useLocationOptions } from '@/app/socialListening/composables/useLocationOptions';
import {
  LISTENING_FILTER_TOOLTIPS,
  LOCATION_FILTER_PLACEHOLDER,
  SEARCH_SELECT_FILTER_OPTIONS_LIMIT,
} from '@/app/socialListening/constants/listeningFilters';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  dataTest: {
    type: String,
    default: 'LocationFilter',
  },
  keyValue: {
    type: [String, Number],
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  countriesOnly: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: String,
    default: LISTENING_FILTER_TOOLTIPS.LOCATIONS,
  },
  filterPanelScope: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(['update:modelValue']);

const { cityStateOptions, lookupLocationsInOptions, loadCityStateOptions } = useLocationOptions();

const displayOptions = computed(() => {
  if (!props.countriesOnly) return cityStateOptions.value;
  return cityStateOptions.value.map((option) => ({
    ...option,
    disabled: option.value?.city != null || option.value?.administrativeDivision != null,
  }));
});
const locationSearchListKey = ref(0);

onMounted(async () => {
  await loadCityStateOptions();
});

const internalLocations = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    const lookedUpLocations = newValue?.length ? lookupLocationsInOptions(newValue) : newValue;
    emit('update:modelValue', lookedUpLocations);
  },
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue?.length && cityStateOptions.value?.length) {
      const lookedUp = lookupLocationsInOptions(newValue);
      if (JSON.stringify(lookedUp) !== JSON.stringify(newValue)) {
        emit('update:modelValue', lookedUp);
      }
    }
  },
  { deep: true },
);

defineExpose({
  resetKey: () => {
    locationSearchListKey.value += 1;
  },
  lookupLocationsInOptions,
});
</script>

<template>
  <FilterPanel
    :key="keyValue ?? locationSearchListKey"
    title="Location"
    filters="locations"
    :scope="filterPanelScope"
    :tooltip="tooltip"
    :data-test="dataTest"
    no-remember
  >
    <SearchSelectList
      v-model="internalLocations"
      multiple
      searchable
      :options="displayOptions"
      :display-option-limit="SEARCH_SELECT_FILTER_OPTIONS_LIMIT"
      :search-placeholder="LOCATION_FILTER_PLACEHOLDER"
      :disabled="disabled"
      disable-select-all
    >
      <template #optionLabel="{ option }">
        <div>{{ option.displayName }}</div>
      </template>
    </SearchSelectList>
  </FilterPanel>
</template>
